import { useCallback, useEffect, useState } from "react";
import { FileDown, Plus, Search, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, DOMAINES } from "@/lib/api";
import MaterielHistorique from "@/components/MaterielHistorique";
import SondesClient from "@/components/SondesClient";
import ClientImport from "@/pages/admin/ClientImport";
import ClientEtiquettes from "@/pages/admin/ClientEtiquettes";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

export default function AdminClients() {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [selected, setSelected] = useState(null);
  const [contact, setContact] = useState(null);
  const [site, setSite] = useState({ nom: "", adresse: "" });
  const [equip, setEquip] = useState({ domaine: "electricite", designation: "", site_id: "s1", numero_serie: "", date_pose: "" });
  const [histo, setHisto] = useState(null);
  const [risques, setRisques] = useState([]);

  const estARisque = (m) =>
    risques.some((r) => (r.materiel_id && r.materiel_id === m.id) || (r.designation || "").toLowerCase() === (m.designation || "").toLowerCase());

  const ouvrirHistorique = async (m) => {
    try {
      const { data } = m.id
        ? await api.get(`/admin/materiel/${m.id}/historique`)
        : await api.get(`/admin/clients/${selected.axonaut_id}/materiel-historique`, {
            params: { designation: m.designation, ...(m.site_id ? { site_id: m.site_id } : {}) },
          });
      setHisto(data);
    } catch (e) {
      toast.error(errMsg(e, "Historique indisponible"));
    }
  };

  const rapportAnnuel = async () => {
    try {
      const { data } = await api.get(`/admin/clients/${selected.axonaut_id}/rapport-annuel.pdf`, {
        params: { mois: 12 },
        responseType: "blob",
      });
      const url = URL.createObjectURL(data);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 30000);
    } catch (e) {
      toast.error(errMsg(e, "Rapport indisponible"));
    }
  };

  const loadContact = useCallback(async (axonautId) => {
    const { data } = await api.get(`/admin/clients/${axonautId}`);
    setContact(data);
    const { data: risque } = await api.get("/admin/materiel/a-risque");
    setRisques((risque.items || []).filter((r) => String(r.axonaut_id) === String(axonautId)));
  }, []);

  const search = async () => {
    if (!q.trim()) return;
    const { data } = await api.get("/admin/clients/search", { params: { q } });
    setResults(data);
    if (!data.length) toast.error("Aucun client trouvé");
  };

  useEffect(() => {
    if (selected) loadContact(selected.axonaut_id);
  }, [selected, loadContact]);

  const addSite = async () => {
    if (!site.nom.trim()) return toast.error("Nom du site requis");
    await api.post("/admin/sites", { axonaut_id: selected.axonaut_id, ...site });
    setSite({ nom: "", adresse: "" });
    loadContact(selected.axonaut_id);
    toast.success("Site ajouté");
  };

  const addEquip = async () => {
    if (!equip.designation.trim()) return toast.error("Désignation requise");
    await api.post("/admin/materiel", { axonaut_id: selected.axonaut_id, ...equip });
    setEquip({ ...equip, designation: "", numero_serie: "" });
    loadContact(selected.axonaut_id);
    toast.success("Matériel ajouté");
  };

  return (
    <div className="space-y-6" data-testid="admin-clients">
      <div>
        <div className="bk-label mb-2">Clients · sites · QR matériel</div>
        <h1 className="font-display text-3xl font-black tracking-tighter text-slate-900">Parc client</h1>
        <p className="text-sm text-slate-600 mt-2">
          Les sites remontent d'Axonaut (champ personnalisé « Sites » au format <code className="font-mono-bk">Nom | Adresse</code> et adresses des
          contrats), complétables ici. Chaque matériel génère un QR code à coller sur l'équipement : le client scanne et arrive sur la fiche
          pré-remplie du bon domaine.
        </p>
      </div>

      <ClientImport />

      <div className="flex gap-2">
        <Input
          data-testid="client-search-input"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && search()}
          placeholder="Nom du client ou email"
          className="h-12"
        />
        <Button data-testid="client-search-btn" onClick={search} className="h-12 bg-slate-900 hover:bg-slate-700">
          <Search size={16} />
        </Button>
      </div>

      {results.length > 0 && !selected && (
        <div className="space-y-2">
          {results.map((r) => (
            <button
              key={r.axonaut_id}
              data-testid={`client-result-${r.axonaut_id}`}
              onClick={() => setSelected(r)}
              className="w-full text-left rounded-xl border border-slate-200 bg-white px-4 py-3 hover:border-[#005CFF] transition-colors"
            >
              <div className="text-sm font-semibold text-slate-900">{r.nom}</div>
              <div className="text-xs text-slate-500">
                {r.source} · #{r.axonaut_id}
              </div>
            </button>
          ))}
        </div>
      )}

      {selected && contact && (
        <div className="space-y-6" data-testid="client-detail">
          <div className="flex items-center justify-between">
            <div>
              <div className="font-display text-xl font-bold text-slate-900">{contact.nom}</div>
              <div className="text-xs text-slate-500">
                Axonaut #{contact.axonaut_id} · {contact.contrat?.libelle}
              </div>
            </div>
            <Button variant="outline" onClick={() => { setSelected(null); setContact(null); }}>Changer de client</Button>
          </div>

          <Button data-testid="rapport-annuel-btn" onClick={rapportAnnuel} variant="outline" className="w-full sm:w-auto">
            <FileDown size={15} className="mr-2" /> Rapport de parc (12 mois) en PDF
          </Button>

          <div className="grid lg:grid-cols-2 gap-6">
            <div className="rounded-xl border border-slate-200 bg-white p-5 space-y-3">
              <div className="bk-label">Sites ({contact.sites?.length || 0})</div>
              {contact.sites?.map((s) => (
                <div key={s.id} className="flex items-start justify-between gap-2 border-b border-slate-100 pb-2" data-testid={`site-row-${s.id}`}>
                  <div>
                    <div className="text-sm font-semibold text-slate-900">{s.nom}</div>
                    <div className="text-xs text-slate-500">{s.adresse || "—"} · {s.source || "axonaut"}</div>
                  </div>
                  {s.source === "bkprod" && (
                    <button
                      data-testid={`site-delete-${s.id}`}
                      onClick={async () => {
                        await api.delete(`/admin/sites/${s.id}`);
                        loadContact(selected.axonaut_id);
                      }}
                      className="text-slate-300 hover:text-red-500"
                    >
                      <Trash2 size={14} />
                    </button>
                  )}
                </div>
              ))}
              <Input data-testid="site-nom-input" value={site.nom} onChange={(e) => setSite({ ...site, nom: e.target.value })} placeholder="Nom du site" className="h-10" />
              <Input data-testid="site-adresse-input" value={site.adresse} onChange={(e) => setSite({ ...site, adresse: e.target.value })} placeholder="Adresse" className="h-10" />
              <Button data-testid="site-add-btn" onClick={addSite} variant="outline" size="sm">
                <Plus size={14} className="mr-1" /> Ajouter un site
              </Button>
            </div>

            <div className="rounded-xl border border-slate-200 bg-white p-5 space-y-3">
              <div className="bk-label">Matériel installé ({contact.materiel?.length || 0})</div>
              <select data-testid="equip-domaine-select" value={equip.domaine} onChange={(e) => setEquip({ ...equip, domaine: e.target.value })} className="w-full h-10 border border-slate-200 rounded-lg px-3 text-sm">
                {DOMAINES.map((d) => (
                  <option key={d.key} value={d.key}>{d.label}</option>
                ))}
              </select>
              <select data-testid="equip-site-select" value={equip.site_id} onChange={(e) => setEquip({ ...equip, site_id: e.target.value })} className="w-full h-10 border border-slate-200 rounded-lg px-3 text-sm">
                {contact.sites?.map((s) => (
                  <option key={s.id} value={s.id}>{s.nom}</option>
                ))}
              </select>
              <Input data-testid="equip-designation-input" value={equip.designation} onChange={(e) => setEquip({ ...equip, designation: e.target.value })} placeholder="Désignation (ex : NVR Hikvision DS-7616)" className="h-10" />
              <div className="flex gap-2">
                <Input data-testid="equip-serie-input" value={equip.numero_serie} onChange={(e) => setEquip({ ...equip, numero_serie: e.target.value })} placeholder="N° de série (optionnel)" className="h-10" />
                <Input data-testid="equip-datepose-input" type="date" value={equip.date_pose} onChange={(e) => setEquip({ ...equip, date_pose: e.target.value })} className="h-10 w-40" />
              </div>
              <Button data-testid="equip-add-btn" onClick={addEquip} variant="outline" size="sm">
                <Plus size={14} className="mr-1" /> Ajouter le matériel
              </Button>
            </div>
          </div>

          <SondesClient axonautId={selected.axonaut_id} sites={contact.sites || []} />

          <ClientEtiquettes contact={contact} axonautId={selected.axonaut_id} estARisque={estARisque} onHistorique={ouvrirHistorique} />
        </div>
      )}
      <MaterielHistorique data={histo} onClose={() => setHisto(null)} />
    </div>
  );
}
